import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './SkillTreeRPG.css';

const SkillTreeRPG = ({ isOpen, onClose, inline = false }) => {
  const [stats, setStats] = useState(null);
  const [skillTree, setSkillTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [activeTree, setActiveTree] = useState('algorithm');

  useEffect(() => {
    if (isOpen || inline) {
      fetchData();
    }
  }, [isOpen, inline]);

  const fetchData = async () => {
    try {
      const [statsRes, treeRes] = await Promise.all([
        api.get('/api/gamification/stats'),
        api.get('/api/gamification/skills/tree')
      ]);
      setStats(statsRes.data);
      setSkillTree(treeRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching skill tree data:', error);
      setLoading(false);
    }
  };

  const spendSkillPoints = async (skillTreeName, points) => {
    try {
      const response = await api.post('/api/gamification/skills/spend', {
        skillTree: skillTreeName,
        points: points
      });
      setStats(response.data);
      alert(`Successfully invested ${points} skill points in ${skillTreeName}!`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to spend skill points');
    }
  };

  const getCharacterClassIcon = (characterClass) => {
    const icons = {
      NOVICE: '🌱',
      INTERMEDIATE: '🌿',
      ADVANCED: '🌳',
      EXPERT: '🏆',
      MASTER: '👑',
      GRANDMASTER: '⭐'
    };
    return icons[characterClass] || '🌱';
  };

  const getExperiencePercentage = () => {
    if (!stats || !stats.experienceToNextLevel || stats.experienceToNextLevel === 0) return 0;
    // Use currentLevelExperience (XP within current level) instead of total experiencePoints
    const currentXP = stats.currentLevelExperience || 0;
    const percentage = (currentXP / stats.experienceToNextLevel) * 100;
    return Math.min(Math.max(percentage, 0), 100); // Clamp between 0 and 100
  };

  if (!isOpen && !inline) return null;

  // Inline version for dashboard (no modal overlay)
  if (inline) {
    return (
      <div className="skill-tree-inline">
        {loading ? (
          <div className="loading">Loading skill tree...</div>
        ) : (
          <>
            {/* Help Section */}
            <div style={{ marginBottom: '20px', padding: '15px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', color: 'white' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>🎮 How RPG Skill Tree Works</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
                <li><strong>Earn XP:</strong> Solve coding problems to gain experience points</li>
                <li><strong>Level Up:</strong> Reach {stats?.experienceToNextLevel || 100} XP to advance to the next level</li>
                <li><strong>Skill Points:</strong> Get 1 skill point per level to invest in specializations</li>
                <li><strong>Specializations:</strong> Focus on Algorithms, Data Structures, or Coding Practice</li>
              </ul>
            </div>

            {/* Character Stats Panel - Redesigned */}
            <div style={{ 
              background: 'white', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              {/* Stats Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: 'white'
                }} title="Your current rank based on total XP earned">
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                    {getCharacterClassIcon(stats?.characterClass || 'NOVICE')}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>CLASS</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {stats?.characterClass || 'NOVICE'}
                  </div>
                </div>

                <div style={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: 'white'
                }} title="Current level - earn XP by solving problems">
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>LEVEL</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                    {stats?.level || 1}
                  </div>
                </div>

                <div style={{ 
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: 'white'
                }} title="Skill points available to invest in specializations">
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎯</div>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>SKILL POINTS</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                    {stats?.availableSkillPoints || 0}
                  </div>
                </div>
              </div>

              {/* XP Progress Bar - Fixed Layout */}
              <div style={{ marginTop: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Experience Progress
                  </span>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#7c3aed',
                    whiteSpace: 'nowrap'
                  }}>
                    {stats?.currentLevelExperience || 0} / {stats?.experienceToNextLevel || 100} XP
                  </span>
                </div>
                <div style={{ 
                  width: '100%',
                  height: '32px',
                  background: '#e5e7eb',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <div style={{ 
                    width: `${getExperiencePercentage()}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '16px',
                    transition: 'width 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ 
                      color: 'white', 
                      fontSize: '13px', 
                      fontWeight: 'bold',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      whiteSpace: 'nowrap'
                    }}>
                      {Math.round(getExperiencePercentage())}%
                    </span>
                  </div>
                </div>
                <div style={{ 
                  marginTop: '6px', 
                  fontSize: '12px', 
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  {(stats?.experienceToNextLevel || 100) - (stats?.currentLevelExperience || 0)} XP needed to reach Level {(stats?.level || 1) + 1}
                </div>
              </div>
            </div>

            {/* Skill Trees */}
            <div className="skill-trees">
              <div className="tree-tabs">
                <button 
                  className={`tree-tab ${activeTree === 'algorithm' ? 'active' : ''}`}
                  onClick={() => setActiveTree('algorithm')}
                >
                  🧠 Algorithms
                </button>
                <button 
                  className={`tree-tab ${activeTree === 'datastructure' ? 'active' : ''}`}
                  onClick={() => setActiveTree('datastructure')}
                >
                  📊 Data Structures
                </button>
                <button 
                  className={`tree-tab ${activeTree === 'coding' ? 'active' : ''}`}
                  onClick={() => setActiveTree('coding')}
                >
                  💻 Coding
                </button>
              </div>

              <div className="skill-nodes">
                {skillTree && Object.entries(skillTree).map(([name, skill]) => {
                  if (!skill.skillTree || skill.skillTree.toLowerCase() !== activeTree) return null;
                  const progress = skill.pointsInvested;
                  const maxLevel = 10;
                  
                  return (
                    <div key={name} className="skill-node">
                      <div className="skill-icon">{skill.icon}</div>
                      <div className="skill-info">
                        <div className="skill-name">{skill.displayName}</div>
                        <div className="skill-progress">
                          <div className="skill-progress-bar">
                            <div 
                              className="skill-progress-fill" 
                              style={{ width: `${(progress / maxLevel) * 100}%` }}
                            ></div>
                          </div>
                          <span className="skill-level">{progress} / {maxLevel}</span>
                        </div>
                        <div className="skill-description">{skill.description}</div>
                        {stats.availableSkillPoints > 0 && progress < maxLevel && (
                          <button 
                            className="invest-btn"
                            onClick={() => spendSkillPoints(name, 1)}
                          >
                            Invest 1 Point
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Modal version (original)
  return (
    <div className="skill-tree-modal-overlay" onClick={onClose}>
      <div className="skill-tree-modal" onClick={(e) => e.stopPropagation()}>
        <div className="skill-tree-header">
          <h2>🎮 RPG Skill Tree</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="loading">Loading skill tree...</div>
        ) : (
          <>
            {/* Character Stats Panel */}
            <div className="character-stats">
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-icon">{getCharacterClassIcon(stats?.characterClass)}</span>
                  <div>
                    <div className="stat-label">Level {stats?.level}</div>
                    <div className="stat-value">{stats?.characterClass}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">⚡</span>
                  <div>
                    <div className="stat-label">Skill Points</div>
                    <div className="stat-value highlight">{stats?.availableSkillPoints}</div>
                  </div>
                </div>
              </div>

              <div className="experience-bar">
                <div className="exp-label">
                  EXP: {stats?.currentLevelExperience} / {stats?.experienceToNextLevel}
                </div>
                <div className="exp-bar-bg">
                  <div 
                    className="exp-bar-fill" 
                    style={{ width: `${getExperiencePercentage()}%` }}
                  ></div>
                </div>
              </div>

              <div className="quick-stats">
                <div className="quick-stat">🎯 {stats?.problemsSolved} Problems Solved</div>
                <div className="quick-stat">🔥 {stats?.currentStreak} Day Streak</div>
                <div className="quick-stat">✨ {stats?.codeSmellScore?.toFixed(1)} Code Quality</div>
              </div>
            </div>

            {/* Skill Tree Tabs */}
            <div className="skill-tree-tabs">
              <button 
                className={activeTree === 'algorithm' ? 'tab active' : 'tab'}
                onClick={() => setActiveTree('algorithm')}
              >
                🧠 Algorithm ({stats?.algorithmSkillPoints})
              </button>
              <button 
                className={activeTree === 'debugging' ? 'tab active' : 'tab'}
                onClick={() => setActiveTree('debugging')}
              >
                🐛 Debugging ({stats?.debuggingSkillPoints})
              </button>
              <button 
                className={activeTree === 'optimization' ? 'tab active' : 'tab'}
                onClick={() => setActiveTree('optimization')}
              >
                ⚡ Optimization ({stats?.optimizationSkillPoints})
              </button>
            </div>

            {/* Skill Tree Content */}
            <div className="skill-tree-content">
              {skillTree && skillTree[activeTree] && (
                <div className="skill-tree">
                  <div className="tree-header">
                    <h3>{skillTree[activeTree].icon} {skillTree[activeTree].name}</h3>
                    <p>{skillTree[activeTree].description}</p>
                  </div>

                  <div className="skills-grid">
                    {skillTree[activeTree].skills.map((skill) => (
                      <div 
                        key={skill.id} 
                        className="skill-node"
                        onClick={() => setSelectedSkill(skill)}
                      >
                        <div className="skill-icon">⭐</div>
                        <div className="skill-name">{skill.name}</div>
                        <div className="skill-cost">Cost: {skill.cost} SP</div>
                      </div>
                    ))}
                  </div>

                  {selectedSkill && (
                    <div className="skill-detail">
                      <h4>{selectedSkill.name}</h4>
                      <p>Invest skill points to improve this ability</p>
                      <button 
                        className="invest-btn"
                        onClick={() => spendSkillPoints(activeTree, selectedSkill.cost)}
                        disabled={stats.availableSkillPoints < selectedSkill.cost}
                      >
                        Invest {selectedSkill.cost} Skill Points
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Statistics Summary */}
            <div className="stats-summary">
              <div className="summary-item">
                <span className="label">Easy:</span>
                <span className="value">{stats?.easySolved}</span>
              </div>
              <div className="summary-item">
                <span className="label">Medium:</span>
                <span className="value">{stats?.mediumSolved}</span>
              </div>
              <div className="summary-item">
                <span className="label">Hard:</span>
                <span className="value">{stats?.hardSolved}</span>
              </div>
              <div className="summary-item">
                <span className="label">Expert:</span>
                <span className="value">{stats?.expertSolved}</span>
              </div>
              <div className="summary-item">
                <span className="label">Success Rate:</span>
                <span className="value">{stats?.successRate?.toFixed(1)}%</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SkillTreeRPG;


